import { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { DataFilters } from "../components/DataFilters";
import { downloadCsv } from "../domain/csv";
import {
  ageBandForGrade,
  formatBirthdayLabel,
  gradeLabel,
  matchesAgeFilter,
  staff,
  type AgeFilter,
  type School,
} from "../domain/data";
import {
  PERMISSION_EXPORT_HEADERS,
  permissionExportValues,
} from "../domain/permissionForm";
import {
  submissionDate,
  useForms,
  type FormSubmission,
} from "../state/forms";
import { PrototypeBanner, StaffNav } from "../components/ui";

const HIDDEN_GRID_COLUMNS = new Set([
  "Guardian email",
  "Guardian phone",
  "Emergency",
  "Emergency phone",
]);

const FIELD_HEADERS = [
  "Submitted",
  "Student",
  "Birthday",
  "Email",
  "School district",
  "Grade",
  "Age",
  "MS/HS",
  "Guardian",
  "Guardian email",
  "Guardian phone",
  "Emergency",
  "Emergency phone",
  "Permission slips",
  "Medical",
  "Conduct",
];

function fieldValues(row: FormSubmission): Array<string | number> {
  const { answers } = row;
  return [
    row.submittedLabel,
    `${answers.given} ${answers.family}`,
    formatBirthdayLabel(answers.birthday),
    answers.email,
    answers.school,
    gradeLabel(answers.grade),
    answers.age,
    ageBandForGrade(answers.grade),
    answers.guardianName,
    answers.guardianEmail,
    answers.guardianPhone,
    answers.emergencyName,
    answers.emergencyPhone,
    answers.permissionSlips ? "yes" : "no",
    answers.medical ? "yes" : "no",
    answers.conduct ? "yes" : "no",
  ];
}

export function StaffForm() {
  const { formId = "" } = useParams();
  const { getForm, submissionsFor } = useForms();
  const form = getForm(formId);
  const [age, setAge] = useState<AgeFilter>("all");
  const [school, setSchool] = useState<School | "all">("all");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");

  const allRows = form ? submissionsFor(form.id) : [];
  const rows = useMemo(
    () =>
      allRows.filter((row) => {
        const { answers } = row;
        if (!matchesAgeFilter(answers.grade, age)) return false;
        if (school !== "all" && answers.school !== school) return false;
        const submitted = submissionDate(row);
        if (from && submitted && submitted < from) return false;
        if (to && submitted && submitted > to) return false;
        return true;
      }),
    [allRows, age, school, from, to],
  );

  if (!form) {
    return (
      <div className="app">
        <PrototypeBanner />
        <main className="shell">
          <p>Form not found.</p>
          <Link to="/staff/forms">Back to forms</Link>
        </main>
      </div>
    );
  }

  const extraHeaders = form.copy.extraQuestions.map((item) => item.label);
  const headers = [...FIELD_HEADERS, ...PERMISSION_EXPORT_HEADERS, ...extraHeaders];
  const visibleIndexes = headers.flatMap((header, index) =>
    HIDDEN_GRID_COLUMNS.has(header) ? [] : [index],
  );
  const visibleHeaders = visibleIndexes.map((index) => headers[index]!);
  const yearForm = form;
  const elemCount = rows.filter((row) => ageBandForGrade(row.answers.grade) === "ELEM").length;
  const msCount = rows.filter((row) => ageBandForGrade(row.answers.grade) === "MS").length;
  const hsCount = rows.filter((row) => ageBandForGrade(row.answers.grade) === "HS").length;

  function extraValues(row: FormSubmission): string[] {
    return yearForm.copy.extraQuestions.map(
      (item) => row.answers.extraAnswers?.[item.id] ?? "",
    );
  }

  function exportCsv() {
    downloadCsv(
      `${yearForm.year}-lift-form.csv`,
      headers,
      rows.map((row) => [
        ...fieldValues(row),
        ...permissionExportValues(row.answers.permission),
        ...extraValues(row),
      ]),
    );
  }

  function clearFilters() {
    setAge("all");
    setSchool("all");
    setFrom("");
    setTo("");
  }

  return (
    <div className="app">
      <PrototypeBanner />
      <main className="shell shell--wide">
        <StaffNav />
        <p className="meta">
          Staff · {staff.given} {staff.family} · {form.year}
        </p>
        <h1>{form.name}</h1>
        <p className="lede">
          Filter by middle school or high school, district, and submitted date.
          Guardian email, guardian phone, and emergency contacts stay off this
          grid. Export the CSV for the full set of fields.
        </p>

        <DataFilters
          age={age}
          onAge={setAge}
          school={school}
          onSchool={setSchool}
          from={from}
          onFrom={setFrom}
          to={to}
          onTo={setTo}
          showDates
          onClear={clearFilters}
        />

        <div className="actions actions-split" style={{ marginTop: 0, marginBottom: "1rem" }}>
          <div className="actions-group">
            <button type="button" onClick={exportCsv} disabled={rows.length === 0}>
              Export CSV
            </button>
            <Link className="btn" to="/staff/students/upload">
              Import Students
            </Link>
          </div>
          <div className="actions-group">
            <Link className="btn ghost" to="/staff/forms/new">
              New school year
            </Link>
            <Link className="btn ghost" to={`/staff/forms/${form.id}/edit`}>
              {form.status === "open" ? "Design form" : "View design"}
            </Link>
            <Link className="btn ghost" to="/staff/forms">
              All forms
            </Link>
          </div>
        </div>

        <div className="grid-2">
          <div className="wire">
            <div className="meta">Showing</div>
            <strong style={{ fontSize: "1.5rem" }}>
              {rows.length} of {allRows.length}
            </strong>
            <div className="anno">
              Elem {elemCount} · MS {msCount} · HS {hsCount}
            </div>
          </div>
          <div className="wire dashed">
            <div className="meta">Status</div>
            <strong style={{ fontSize: "1.5rem" }}>
              {form.status === "open" ? "Open" : "Saved"}
            </strong>
            <div className="anno">
              {form.status === "open"
                ? "Export uses the filtered rows."
                : `Archived ${form.archivedLabel}. Responses stay here.`}
            </div>
          </div>
        </div>

        <h2>Submissions</h2>
        {rows.length === 0 ? (
          <div className="wire dashed">
            <div className="meta">No responses match these filters</div>
          </div>
        ) : (
          <div className="table-wrap table-wrap--wide">
            <table className="table">
              <thead>
                <tr>
                  {visibleHeaders.map((header) => (
                    <th key={header}>{header}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => {
                  const values = [
                    ...fieldValues(row),
                    ...permissionExportValues(row.answers.permission),
                    ...extraValues(row),
                  ];
                  return (
                    <tr key={row.id}>
                      {visibleIndexes.map((index) => (
                        <td key={`${row.id}-${index}`}>{values[index]}</td>
                      ))}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </main>
    </div>
  );
}
